import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ChakraProvider, Box, Spinner, Center } from '@chakra-ui/react';
import theme from './theme';
import AuthContext from './context/AuthContext';
import Layout from './components/Layout';
import { setAuthToken, removeAuthToken, refreshAccessToken } from './services/api';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import CharacterCreator from './pages/CharacterCreator';
import CharacterEdit from './pages/CharacterEdit';
import CharacterView from './pages/CharacterView';
import SessionView from './pages/SessionView';
import Chat from './pages/Chat';
import MySessions from './pages/MySessions';
import NotFound from './pages/NotFound';

// Access tokens live in memory only — they're short-lived (15min) and the
// refresh token is held in an httpOnly cookie the JS can't touch. The
// `user` object (id/username/role) is fine to keep in localStorage as a UX
// nicety: it isn't a credential.

function App() {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [bootstrapping, setBootstrapping] = useState(true);

  // On mount, try to swap the refresh cookie for a fresh access token. If
  // there's no cookie or it's expired we drop to /login.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const fresh = await refreshAccessToken();
      if (cancelled) return;
      if (fresh) {
        setAuthToken(fresh);
        setToken(fresh);
      } else {
        removeAuthToken();
        setToken(null);
        setUser(null);
        localStorage.removeItem('user');
      }
      setBootstrapping(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cross-component auth signals from the api interceptor.
  useEffect(() => {
    const onRefreshed = (e) => {
      if (e.detail?.token) setToken(e.detail.token);
    };
    const onLogout = () => {
      setToken(null);
      setUser(null);
      localStorage.removeItem('user');
    };
    window.addEventListener('auth:token-refreshed', onRefreshed);
    window.addEventListener('auth:logout', onLogout);
    return () => {
      window.removeEventListener('auth:token-refreshed', onRefreshed);
      window.removeEventListener('auth:logout', onLogout);
    };
  }, []);

  // Keep the axios default + the cached user mirror in sync with state.
  useEffect(() => {
    if (token) {
      setAuthToken(token);
    } else {
      removeAuthToken();
    }
  }, [token]);

  // Proactively rotate the access token while the app is open so it never sits
  // expired during idle (e.g. reading a long reply before typing the next post).
  // Access tokens live ~15min, so refresh a little early. This updates `token` via
  // setToken; the axios instance adopts it (effect above) and Chat no longer
  // refetches on token change, so it can't disturb the chat view. refreshAccessToken()
  // de-dupes in-flight calls, so this never races the reactive 401-refresh.
  useEffect(() => {
    if (!token) return undefined;
    const REFRESH_INTERVAL_MS = 13 * 60 * 1000; // comfortably under the 15-min TTL
    let lastRotate = Date.now();

    const rotate = async () => {
      lastRotate = Date.now();
      const fresh = await refreshAccessToken();
      if (fresh) {
        setAuthToken(fresh);
        setToken(fresh);
      }
      // On failure the reactive 401 interceptor / auth:logout flow still covers us.
    };

    const timer = setInterval(rotate, REFRESH_INTERVAL_MS);
    // Background tabs throttle/pause timers, so also rotate when the tab returns to
    // the foreground after being away a while (guarded so quick tab-switches don't spam).
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRotate > 5 * 60 * 1000) {
        rotate();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  if (bootstrapping) {
    return (
      <ChakraProvider theme={theme}>
        <Center minH="100vh"><Spinner size="xl" /></Center>
      </ChakraProvider>
    );
  }

  return (
    <ChakraProvider theme={theme}>
      <AuthContext.Provider value={{ token, setToken, user, setUser }}>
        <Router>
          <Box minHeight="100vh">
            <Routes>
              <Route path="/login" element={!token ? <Login /> : <Navigate to="/" />} />

              <Route path="/" element={token ? <Layout /> : <Navigate to="/login" />}>
                <Route index element={<Dashboard />} />
                <Route path="admin" element={user?.role === 'admin' ? <Admin /> : <Navigate to="/" />} />
                <Route path="create-character" element={<CharacterCreator />} />
                <Route path="edit-character/:id" element={<CharacterEdit />} />
                <Route path="view-character/:id" element={<CharacterView />} />
                <Route path="view-session/:sessionId" element={<SessionView />} />
                <Route path="chat/:sessionId" element={<Chat />} />
                <Route path="my-sessions" element={<MySessions />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Box>
        </Router>
      </AuthContext.Provider>
    </ChakraProvider>
  );
}

export default App;
